import type { ComponentType } from 'react';
import {
  IconLayoutDashboard,
  IconHeartbeat,
  IconActivity,
  IconAtom,
  IconBrain,
  IconBone,
  IconBolt,
  IconSettings,
  IconSearch,
  IconChartBar,
  IconMapPin,
  IconUsers,
  IconStethoscope,
  IconVideo,
  IconPoint,
  IconLayersIntersect,
  IconTestPipe2,
  IconMail,
} from '@tabler/icons-react';

import { MenuDto } from 'types/menu';
import { NavItemType } from 'types';
import { ECHOS_STUDIES_BASE_PATH } from 'utils/sectionPaths';

// ==============================|| MENU TRANSFORMER ||============================== //

/**
 * True when the menu path is unset (DB NULL or empty string).
 * Empty string would otherwise make filterActionsFromTree drop the group.
 */
function hasNoPath(p: string | null | undefined): boolean {
  return p == null || p === '';
}

/**
 * Canonical workspace base for Administrative Tools when the "new" child path varies in the DB
 * (e.g. /administrative-tools/users?tab=new vs /administrative-tools/user/new).
 */
function administrativeToolsWorkspacePath(actionPath: string): string {
  const noQuery = actionPath.split('?')[0].replace(/\/$/, '') || actionPath;
  const m = noQuery.match(/^\/administrative-tools\/([^/]+)(?:\/new)?$/);
  if (!m) return noQuery;
  const raw = m[1];
  const singularToPlural: Record<string, string> = {
    user: 'users',
    location: 'locations',
    doctor: 'doctors',
  };
  const segment = singularToPlural[raw] ?? raw;
  return `/administrative-tools/${segment}`;
}

/**
 * Derives the workspace URL for a module that has no path of its own
 * (first child with a path; often an action such as create_report or .list).
 *
 * - Deeper action paths (3+ path segments) share a workspace: strip the last segment
 *   e.g. /cardiac_studies/nuclear_stress_test/create_report → /cardiac_studies/nuclear_stress_test
 * - If the last segment is `list`, the URL is the final page (admin standard), do not strip
 *   (non-admin) e.g. some list-heavy workspaces keep the full path
 * - Administrative Tools: prefer the `.new` child and use the path **without** query so the SPA
 *   default tab (Create) applies; the DB often orders `.list` first by sort_order.
 * - Shallow list routes (exactly 2 path segments) keep the full path
 */
function deriveModuleUrl(item: MenuDto): string | null {
  if (!hasNoPath(item.path)) return item.path ?? null;
  const withPath = (item.children ?? []).filter((c) => c.path);
  if (!withPath.length) return null;

  const adminNew = withPath.find(
    (c) => c.path!.startsWith('/administrative-tools/') && c.key.split('.').pop() === 'new'
  );
  if (adminNew?.path) {
    return administrativeToolsWorkspacePath(adminNew.path);
  }

  const firstChild = withPath[0]!;
  const p = firstChild.path;
  if (!p) return null;
  const segs = p.split('/').filter(Boolean);
  if (segs.length <= 2) {
    return p;
  }
  if (segs[segs.length - 1] === 'list') {
    return p;
  }
  return `/${segs.slice(0, -1).join('/')}`;
}

/**
 * Recursively strips leaf nodes at depth ≥ 2 (action items).
 *
 * Tree depth mapping:
 *   depth 0 → group   (top-level section, parentId = null)  — kept
 *   depth 1 → module  (workspace entry point)               — kept, becomes item if all children stripped
 *   depth 2 → action  (Create Report, View Pending, …)      — stripped from sidebar
 *
 * parentKey: the key of the parent node, used to derive a workspace URL for
 * modules that have no children and no path of their own (e.g. MUGA, NST when
 * the DB has no action rows yet).
 */
function filterActionsFromNode(item: MenuDto, depth = 0, parentKey = ''): MenuDto {
  const childList = item.children ?? [];
  // At depth ≥ 1, drop leaf children (they are action items).
  const keptChildren = childList
    .filter((c) => depth === 0 || (c.children?.length ?? 0) > 0)
    .map((c) => filterActionsFromNode(c, depth + 1, item.key));

  // If the module had action children but none survived the filter,
  // it becomes a leaf item pointing to the workspace URL.
  const allChildrenWereActions =
    childList.length > 0 && keptChildren.length === 0 && depth >= 1;

  // For modules with no children at all and no path, derive the workspace URL
  // from the parent group key + this item's key last segment.
  // e.g. parent="cardiac_studies", item.key="muga" → "/cardiac_studies/muga"
  //      parent="cardiac_studies", item.key="cardiac_studies.nuclear_stress_test"
  //        → "/cardiac_studies/nuclear_stress_test"
  const noChildrenNoPath =
    depth >= 1 && hasNoPath(item.path) && childList.length === 0 && parentKey !== '';

  const resolvedPath = (() => {
    if (allChildrenWereActions) return deriveModuleUrl(item);
    if (noChildrenNoPath) {
      const parentSlug = parentKey.split('.').pop()!;
      const itemSlug   = item.key.split('.').pop()!;
      return `/${parentSlug}/${itemSlug}`;
    }
    // Top-level section with no `path` in DB: use the app’s dedicated route (Echo Studies only for now).
    if (depth === 0 && hasNoPath(item.path) && keptChildren.length > 0 && item.key === 'echos_studies') {
      return ECHOS_STUDIES_BASE_PATH;
    }
    if (!hasNoPath(item.path)) return item.path ?? null;
    return null;
  })();

  return {
    ...item,
    path: resolvedPath,
    children: keptChildren
  };
}

/**
 * Removes action-level leaves (depth ≥ 2) from the menu tree so the sidebar
 * shows only groups and modules. Module nodes that held only action children
 * are converted into clickable items pointing to their workspace URL.
 *
 * Root-level items are limited to structured groups (path === null with
 * children). Standalone root leaves are excluded to avoid an invalid drawer tree.
 */
export function filterActionsFromTree(menus: MenuDto[]): MenuDto[] {
  // Only keep structured hierarchy groups at the root level.
  // - no path (null or '') → parent group with nested modules, not a flat leaf
  // - children > 0         → it has content to show
  const structuredGroups = menus.filter(
    (item) => hasNoPath(item.path) && (item.children?.length ?? 0) > 0
  );
  return structuredGroups.map((item) => filterActionsFromNode(item, 0, ''));
}

/**
 * Maps a menu key (from the API) to a Tabler icon component.
 * The key matches the MenuDto.Key field returned by the backend.
 */
const ICON_MAP: Record<string, ComponentType<any>> = {
  // —— DB root keys (menus.name / menus.key) — keep in sync with Telerad DB ——
  cardiac_studies: IconHeartbeat,
  echos_studies: IconActivity,
  nuclear_studies: IconAtom,
  /** “General” bucket (Nuclear) */
  general: IconLayersIntersect,
  thyroid__parathyroid_scan: IconAtom,
  lung_scan: IconTestPipe2,
  iodine_123_thyroid_scan: IconAtom,

  // Short / alternate slugs
  cardiac: IconHeartbeat,
  echo: IconActivity,
  nuclear: IconAtom,
  ct: IconBrain,
  xray: IconBone,
  'nerve-conduction': IconBolt,

  // Administrative Tools
  administrative_tools: IconSettings,
  'administrative_tools.locations': IconMapPin,
  'administrative_tools.users': IconUsers,
  'administrative_tools.doctors': IconStethoscope,
  'administrative_tools.search': IconSearch,
  'administrative_tools.stats': IconChartBar,
  'administrative_tools.video': IconVideo,
  'administrative_tools.contacts': IconMail,

  dashboard: IconLayoutDashboard
};

/**
 * Icons for any menu key. Exact match first; then prefix rules for dot-keys from DB.
 * Echo registry / cardiac workspaces use `echos_studies.*` / `cardiac_studies.*` etc.
 */
function resolveIcon(key: string): ComponentType<any> {
  if (ICON_MAP[key]) return ICON_MAP[key];
  if (key.startsWith('echos_studies.')) return IconActivity;
  if (key.startsWith('cardiac_studies.')) return IconHeartbeat;
  if (key.startsWith('nuclear_studies.')) return IconAtom;
  if (key.startsWith('administrative_tools.')) {
    if (key.includes('.locations')) return IconMapPin;
    if (key.includes('.users')) return IconUsers;
    if (key.includes('.doctors')) return IconStethoscope;
    return IconSettings;
  }
  if (key.startsWith('general.')) return IconLayersIntersect;
  if (key.startsWith('thyroid__parathyroid_scan.')) return IconAtom;
  if (key.startsWith('lung_scan.')) return IconTestPipe2;
  if (key.startsWith('iodine_123_thyroid_scan.')) return IconAtom;
  return IconPoint;
}

/**
 * Recursively transforms a MenuDto node into a NavItemType.
 *
 * Uses `item.menuId` (database PK) as the React `id` to guarantee uniqueness
 * even when the backend has duplicate `key` values for different entries
 * (e.g., two rows both with key = "search_records").
 */
function transformItem(item: MenuDto): NavItemType {
  const hasChildren = (item.children?.length ?? 0) > 0;

  return {
    id: String(item.menuId),
    title: item.name,
    // All branches with children use collapse so top-level (e.g. Cardiac Studies) is
    // expandable/collapsible in the drawer — not a static `group` heading only.
    type: hasChildren ? 'collapse' : 'item',
    url: item.path ?? undefined,
    // link enables prefix matching in useMenuCollapse (matchPath end:false) so that
    // sub-routes like /administrative-tools/locations/123 keep the parent menu open.
    link: item.path ?? undefined,
    icon: resolveIcon(item.key),
    breadcrumbs: false,
    children: hasChildren
      ? (item.children ?? []).map((child) => transformItem(child))
      : undefined
  };
}

/**
 * Converts the backend MenuDto[] into Berry's NavItemType[] format.
 * The backend returns a pre-nested tree (children already populated).
 */
export function transformMenuDtoToNavItems(menus: MenuDto[]): NavItemType[] {
  return menus.map((item) => transformItem(item));
}

/**
 * First navigable leaf URL in the tree, depth-first — used to pick a landing
 * page for roles that shouldn't default to the dashboard (e.g. Manager).
 */
export function findFirstMenuUrl(items: NavItemType[]): string | undefined {
  for (const item of items) {
    if (item.type === 'item' && item.url) return item.url;
    if (item.children?.length) {
      const found = findFirstMenuUrl(item.children);
      if (found) return found;
    }
  }
  return undefined;
}
